import { Box, ThemeProvider, Video } from "@modernatx/ui-kit-react";
import Hls from "hls.js";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import React from "react";
import { Global } from "theme-ui";

interface VideoData {
  pictures?: {
    src: string;
  }[];
  tracks?: {
    src: string;
    label: string;
    lang: string;
  }[];
  videoUrl?: string;
}

const PlayerPage: React.FC<VideoData & { autoPlay: boolean; loop: boolean; muted: boolean }> = (
  props
) => {
  const { autoPlay, loop, muted, pictures, tracks, videoUrl } = props;
  const hlsPlayer = React.useRef<Hls | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    if (videoUrl && videoRef.current && Hls.isSupported()) {
      hlsPlayer.current = hlsPlayer.current || new Hls();
      hlsPlayer.current.attachMedia(videoRef.current);
      hlsPlayer.current.loadSource(videoUrl);
    }

    return () => {
      hlsPlayer.current?.destroy();
    };
  }, [videoUrl]);

  if (!videoUrl) {
    return <Box>No video data found.</Box>;
  }

  return (
    <ThemeProvider>
      <Global
        styles={{
          html: {
            background: "transparent"
          }
        }}
      />
      <Box
        sx={{
          height: "100vh",
          overflow: "hidden",
          width: "100vw",
          display: "grid"
        }}
      >
        <Video
          ref={videoRef}
          autoPlay={autoPlay}
          crossOrigin="anonymous"
          loop={loop}
          muted={muted}
          poster={pictures?.[0]?.src}
          sx={{
            width: "100%",
            justifyContent: "center"
          }}
          textTracks={tracks?.map((track) => ({
            ...track,
            label: track.label?.replace("(auto-generated)", "")
          }))}
        />
      </Box>
    </ThemeProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const videosApiKey = process.env.VIDEOS_API_KEY;
  const videosEndpoint = process.env.VIDEOS_ENDPOINT;

  if (!videosApiKey || !videosEndpoint) {
    return { notFound: true };
  }

  const { autoplay, loop: loopQuery, muted: mutedQuery, path } = context.query;
  const autoplayParam = Array.isArray(autoplay) ? autoplay[0] : autoplay;
  const autoPlay = autoplayParam !== undefined && autoplayParam !== "0";
  const loopParam = Array.isArray(loopQuery) ? loopQuery[0] : loopQuery;
  const loop = loopParam !== undefined && loopParam !== "0";
  const mutedParam = Array.isArray(mutedQuery) ? mutedQuery[0] : mutedQuery;
  const muted = mutedParam !== undefined && mutedParam !== "0";
  const normalizedPath = Array.isArray(path) ? `/${path.join("/")}` : `/${path}`;

  let apiPath = normalizedPath;

  if (
    normalizedPath &&
    !normalizedPath.startsWith("/pm") &&
    !normalizedPath.startsWith("/pm-dev") &&
    !normalizedPath.startsWith("/mc") &&
    !normalizedPath.startsWith("/mc-dev")
  ) {
    apiPath = `/videos${normalizedPath}`;
  }

  const apiUrl = `${videosEndpoint}?rawPath=${encodeURIComponent(apiPath)}`;

  try {
    const response = await fetch(apiUrl, {
      headers: {
        "x-api-key": videosApiKey
      }
    });

    if (!response.ok) {
      return { notFound: true };
    }

    const videoData = (await response.json()) as VideoData;
    return { props: { ...videoData, autoPlay, loop, muted } };
  } catch (error) {
    return { notFound: true };
  }
};

export default PlayerPage;
